import React from 'react';
import { toDe } from '@/utilites/sku-operations';
import {
  END_OF_SUMMER_SALE_SKU_TAG,
  IS_EASTER_ENABLED,
  IS_END_OF_SUMMER_SALE_ENABLED,
} from '@/constants/campaigns.constants';

interface SkuPriceDisplayProps {
  sku: {
    hot_deal?: boolean;
    retailPrice?: number | null;
    lowestAsk?: number | null;
    highestBid?: number | null;
    sku_tags?: { tags?: string[] } | null;
    // optional: some API payloads might not set lowestAsk, but provide asks list
    asks?: Array<{ value?: number | null }>;
  };
  skupage?: boolean;
  memoizedFilteredAsk?: number;
  showHighestBid?: boolean;
  forceBlackFriday?: boolean;
  forceEaster?: boolean;
  forceEndOfSummerSale?: boolean;
}

export default function SkuPriceDisplay({
  sku,
  skupage = false,
  memoizedFilteredAsk,
  showHighestBid = false,
  forceBlackFriday = false,
  forceEaster: forceEasterProp = false,
  forceEndOfSummerSale: forceEndOfSummerSaleProp = false,
}: SkuPriceDisplayProps) {
  const forceEaster = IS_EASTER_ENABLED && forceEasterProp;
  // A bottle marked for the campaign in Strapi keeps its sale pricing wherever
  // it appears, not just on the campaign page (WEB-1455).
  const isEndOfSummerSale =
    IS_END_OF_SUMMER_SALE_ENABLED &&
    (forceEndOfSummerSaleProp || !!sku.sku_tags?.tags?.includes(END_OF_SUMMER_SALE_SKU_TAG));
  // A campaign strikes through the retail price for every bottle it covers;
  // outside one, only a bottle flagged as a hot deal does.
  const showsRetailPrice = forceBlackFriday || forceEaster || isEndOfSummerSale || !!sku.hot_deal;
  const hasValidRetailPrice = sku.retailPrice != null && sku.retailPrice > 0;

  // Für skupage verwenden wir memoizedFilteredAsk, sonst sku.lowestAsk
  // Fallback: wenn lowestAsk fehlt, nutze den kleinsten Ask aus sku.asks (falls vorhanden)
  const derivedLowestAsk = React.useMemo(() => {
    if (sku.lowestAsk != null && sku.lowestAsk > 0) return sku.lowestAsk;
    if (Array.isArray(sku.asks) && sku.asks.length > 0) {
      const candidates = sku.asks
        .map(a => a?.value ?? null)
        .filter((v): v is number => typeof v === 'number' && isFinite(v) && v > 0);
      if (candidates.length > 0) {
        return Math.min(...candidates);
      }
    }
    return null;
  }, [sku.lowestAsk, sku.asks]);

  const displayPrice =
    skupage && memoizedFilteredAsk != null ? memoizedFilteredAsk : derivedLowestAsk;
  const hasValidDisplayPrice = displayPrice != null && displayPrice > 0;

  const hasValidHighestBid = sku.highestBid != null && sku.highestBid > 0;

  if (skupage) {
    const isDiscountedOnSkuPage =
      showsRetailPrice &&
      hasValidRetailPrice &&
      hasValidDisplayPrice &&
      sku.retailPrice! > displayPrice!;
    return (
      <div className="tw-flex tw-items-center tw-gap-2">
        {showsRetailPrice &&
          hasValidRetailPrice &&
          hasValidDisplayPrice &&
          sku.retailPrice! > displayPrice! && (
            <span className="tw-text-xl tw-font-medium tw-text-gray-500 tw-line-through">
              {toDe(sku.retailPrice!)}
            </span>
          )}
        <h2
          className={`tw-mb-0 tw-text-3xl ${
            isDiscountedOnSkuPage ? 'tw-font-bold' : 'tw-font-medium'
          } tw-text-text`}
          style={{
            fontVariantNumeric: 'tabular-nums',
            ...(isDiscountedOnSkuPage ? { color: '#48a965' } : {}),
          }}
        >
          {hasValidDisplayPrice
            ? toDe(displayPrice!, {
                withDigits: false,
                withSymbol: true,
              })
            : '--'}
        </h2>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-gap-1">
      {showsRetailPrice &&
        hasValidRetailPrice &&
        hasValidDisplayPrice &&
        !showHighestBid &&
        sku.retailPrice! > displayPrice! && (
          <span
            className="tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-line-through"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {toDe(sku.retailPrice!)}
          </span>
        )}

      {(() => {
        const isDiscounted =
          showsRetailPrice &&
          hasValidRetailPrice &&
          hasValidDisplayPrice &&
          !showHighestBid &&
          sku.retailPrice! > displayPrice!;
        const shouldBeGreen = isDiscounted || showHighestBid;
        // Bold like the card title; green marks a discount or the highest bid
        const baseClass =
          'max-nine: tw-mb-0 tw-text-[22px] tw-font-bold tw-text-textBlack max-nine:tw-text-base max-nine:tw-font-[600]';
        return (
          <p
            className={baseClass}
            style={{
              fontVariantNumeric: 'tabular-nums',
              ...(shouldBeGreen ? { color: '#48a965' } : {}),
            }}
          >
            {showHighestBid
              ? hasValidHighestBid
                ? toDe(sku.highestBid!)
                : '--'
              : hasValidDisplayPrice
                ? toDe(displayPrice!)
                : '--'}
          </p>
        );
      })()}
    </div>
  );
}
